import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fmtAUD, fmtCompact, fmtNum, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  ArrowLeft, Wallet, Users, Megaphone, Target, TrendingUp,
  Circle, CheckCircle2, Plus, Trash2, Rocket, Save, Pencil, Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const GOLD = "#D4AF37";
const CYAN = "#00E5FF";
const EMERALD = "#10B981";
const ZINC = "#A1A1AA";

const HORIZON_LABEL = { "1m": "1 Month", "12m": "12 Months", "2y": "2 Years" };

const PRIORITY_STYLE = {
  urgent: "text-red-400 border-red-500/40 bg-red-500/10",
  important: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10",
  medium: "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/10",
  low: "text-zinc-400 border-white/15 bg-white/5",
};
const PRIORITY_CYCLE = ["low", "medium", "important", "urgent"];
const FREQ_CYCLE = ["daily", "weekly", "once"];
const FREQ_LABEL = { daily: "daily", weekly: "weekly", once: "one off" };
const FREQ_CHIP = {
  daily: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10",
  weekly: "text-[#00E5FF] border-[#00E5FF]/30 bg-[#00E5FF]/10",
  once: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="backdrop-blur-2xl bg-black/80 border border-white/10 rounded-sm px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      <p className="text-white font-bold">{fmtNum(payload[0].value)}</p>
    </div>
  );
};

const TrendChart = ({ data, dataKey, color, testid }) => (
  <div className="h-48" data-testid={testid}>
    <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 800, height: 192 }}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
        <defs>
          <linearGradient id={`g-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="week" tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis hide domain={["dataMin - 100", "dataMax + 100"]} />
        <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(255,255,255,0.1)" }} />
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#g-${dataKey})`} />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

export default function BusinessDetail() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(null);
  const [newTask, setNewTask] = useState("");
  const [newFreq, setNewFreq] = useState("daily");
  const [newPriority, setNewPriority] = useState("medium");
  const [metricForm, setMetricForm] = useState({ bank_balance: "", visitors: "", ad_spend: "", reach: "", conversions: "" });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("visitors");
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalDraft, setGoalDraft] = useState({ title: "", target: "", current: "", unit: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/entities/${slug}`);
    setBiz(data);
  }, [slug]);

  useEffect(() => { load().catch(() => toast.error("Failed to load business")); }, [load]);

  const toggleTask = async (id) => {
    try {
      await api.patch(`/tasks/${id}/toggle`);
      load();
    } catch { toast.error("Couldn't update task"); }
  };

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;
    try {
      await api.post(`/entities/${slug}/tasks`, { title: newTask, frequency: newFreq, priority: newPriority });
      setNewTask("");
      toast.success("Task added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteTask = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      load();
    } catch { toast.error("Couldn't delete task"); }
  };

  const saveMetrics = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      Object.entries(metricForm).forEach(([k, v]) => {
        if (v !== "") payload[k] = Number(v);
      });
      await api.post(`/entities/${slug}/metrics`, payload);
      toast.success("Metrics updated");
      setMetricForm({ bank_balance: "", visitors: "", ad_spend: "", reach: "", conversions: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveGoal = async (horizon) => {
    try {
      await api.patch("/goals", {
        slug,
        horizon,
        title: goalDraft.title,
        target: goalDraft.target === "" ? null : Number(goalDraft.target),
        current: goalDraft.current === "" ? null : Number(goalDraft.current),
        unit: goalDraft.unit,
      });
      setEditingGoal(null);
      toast.success("Goal updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!biz) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="business-loading">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const latest = biz.history[biz.history.length - 1] || {};
  const stats = [
    { label: "Bank Balance", value: fmtAUD(biz.bank_balance), icon: Wallet, color: GOLD, key: "bank", chart: null },
    { label: "Visitors / wk", value: fmtCompact(latest.visitors), icon: Users, color: CYAN, key: "visitors", chart: "visitors" },
    { label: "Ad Spend / wk", value: fmtAUD(latest.ad_spend), icon: Megaphone, color: ZINC, key: "ad_spend", chart: "ad_spend" },
    { label: "Reach / wk", value: fmtCompact(latest.reach), icon: TrendingUp, color: EMERALD, key: "reach", chart: "reach" },
    { label: "Conversions / wk", value: fmtNum(latest.conversions), icon: Target, color: GOLD, key: "conversions", chart: "conversions" },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100" data-testid="business-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <img src={biz.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 cyan-glow" />
        <div className="relative max-w-7xl mx-auto px-6 pt-8 pb-14">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#D4AF37] transition-colors duration-200 mb-10" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> All Ventures
          </Link>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              {biz.status === "pre-launch" && (
                <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/40 px-3 py-1 rounded-full mb-4" data-testid="prelaunch-badge">
                  <Rocket className="w-3 h-3" /> Pre-Launch — Building in the dark
                </span>
              )}
              <p className="text-[10px] uppercase tracking-[0.4em] text-zinc-400 mb-2">{biz.tagline}</p>
              <h1 className="font-display text-4xl sm:text-5xl text-white" data-testid="business-name">{biz.name}</h1>
            </div>
            {biz.metrics_updated_at && (
              <p className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="metrics-updated">
                Metrics updated {new Date(biz.metrics_updated_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Stat cards */}
      <section className="max-w-7xl mx-auto px-6 -mt-0 py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="stats-row">
          {stats.map((s, i) => (
            <motion.div
              key={s.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 * i }}
              className={`bg-[#0F0F11] border border-white/10 rounded-md p-5 ${i === 0 ? "col-span-2 md:col-span-1" : ""}`}
              data-testid={`stat-${s.key}`}
            >
              <s.icon className="w-4 h-4 mb-3" style={{ color: s.color }} />
              <p className="text-xl font-bold text-white">{s.value}</p>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trend charts */}
      <section className="max-w-7xl mx-auto px-6 pb-10">
        <h2 className="font-display text-2xl text-white mb-6" data-testid="trends-heading">12-Week Trajectory</h2>
        <Tabs defaultValue="visitors" onValueChange={setActiveTab} data-testid="trend-tabs">
          <TabsList className="bg-[#0F0F11] border border-white/10 rounded-sm mb-6">
            <TabsTrigger value="visitors" className="rounded-sm data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-visitors">Visitors</TabsTrigger>
            <TabsTrigger value="ad_spend" className="rounded-sm data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-ad-spend">Ad Spend</TabsTrigger>
            <TabsTrigger value="reach" className="rounded-sm data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-reach">Reach</TabsTrigger>
            <TabsTrigger value="conversions" className="rounded-sm data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-conversions">Conversions</TabsTrigger>
          </TabsList>
          <div className="bg-[#0F0F11] border border-white/10 rounded-md p-6">
            <TabsContent value="visitors" className="mt-0">{activeTab === "visitors" && <TrendChart data={biz.history} dataKey="visitors" color={CYAN} testid="chart-visitors" />}</TabsContent>
            <TabsContent value="ad_spend" className="mt-0">{activeTab === "ad_spend" && <TrendChart data={biz.history} dataKey="ad_spend" color={ZINC} testid="chart-ad-spend" />}</TabsContent>
            <TabsContent value="reach" className="mt-0">{activeTab === "reach" && <TrendChart data={biz.history} dataKey="reach" color={EMERALD} testid="chart-reach" />}</TabsContent>
            <TabsContent value="conversions" className="mt-0">{activeTab === "conversions" && <TrendChart data={biz.history} dataKey="conversions" color={GOLD} testid="chart-conversions" />}</TabsContent>
          </div>
        </Tabs>
      </section>

      <div className="max-w-7xl mx-auto px-6 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Goals */}
        <section className="bg-[#0F0F11] border border-white/10 rounded-md p-8" data-testid="goals-section">
          <h2 className="font-display text-2xl text-white mb-8" data-testid="goals-heading">Missions & Targets</h2>
          <div className="space-y-8">
            {biz.goals.map((g) => {
              const pct = Math.min(100, Math.round((g.current / g.target) * 100));
              const isEditing = editingGoal === g.horizon;
              return (
                <div key={g.horizon} data-testid={`goal-${g.horizon}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">{HORIZON_LABEL[g.horizon]}</span>
                    <span className="inline-flex items-center gap-3">
                      <span className="text-xs text-zinc-500" data-testid={`goal-progress-${g.horizon}`}>{pct}%</span>
                      {isEditing ? (
                        <button onClick={() => saveGoal(g.horizon)} className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200" data-testid={`goal-save-${g.horizon}`}>
                          <Check className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => { setEditingGoal(g.horizon); setGoalDraft({ title: g.title, target: String(g.target), current: String(g.current), unit: g.unit }); }}
                          className="text-zinc-600 hover:text-[#D4AF37] transition-colors duration-200"
                          data-testid={`goal-edit-${g.horizon}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </span>
                  </div>
                  {isEditing ? (
                    <div className="space-y-3 mb-3" data-testid={`goal-edit-form-${g.horizon}`}>
                      <Input
                        value={goalDraft.title} onChange={(e) => setGoalDraft((d) => ({ ...d, title: e.target.value }))}
                        placeholder="Goal title"
                        className="bg-white/5 border-white/10 text-white rounded-sm text-sm focus-visible:ring-[#D4AF37]"
                        data-testid={`goal-title-input-${g.horizon}`}
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">Progress</label>
                          <Input
                            type="number" min="0" value={goalDraft.current}
                            onChange={(e) => setGoalDraft((d) => ({ ...d, current: e.target.value }))}
                            className="bg-white/5 border-white/10 text-white rounded-sm text-sm focus-visible:ring-[#D4AF37]"
                            data-testid={`goal-current-input-${g.horizon}`}
                          />
                        </div>
                        <div>
                          <label className="text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">Target</label>
                          <Input
                            type="number" min="1" value={goalDraft.target}
                            onChange={(e) => setGoalDraft((d) => ({ ...d, target: e.target.value }))}
                            className="bg-white/5 border-white/10 text-white rounded-sm text-sm focus-visible:ring-[#D4AF37]"
                            data-testid={`goal-target-input-${g.horizon}`}
                          />
                        </div>
                        <div>
                          <label className="text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">Unit</label>
                          <Input
                            value={goalDraft.unit} onChange={(e) => setGoalDraft((d) => ({ ...d, unit: e.target.value }))}
                            placeholder="e.g. sales"
                            className="bg-white/5 border-white/10 text-white rounded-sm text-sm focus-visible:ring-[#D4AF37]"
                            data-testid={`goal-unit-input-${g.horizon}`}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-200 mb-3" data-testid={`goal-title-${g.horizon}`}>{g.title}</p>
                  )}
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, delay: 0.3 }}
                      className="h-full bg-[#D4AF37]"
                    />
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-2 uppercase tracking-wider">
                    {fmtNum(g.current)} / {fmtNum(g.target)} {g.unit}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Tasks */}
        <section className="bg-[#0F0F11] border border-white/10 rounded-md p-8" data-testid="tasks-section">
          <h2 className="font-display text-2xl text-white mb-6" data-testid="tasks-heading">Daily & Weekly Wins</h2>
          <ul className="space-y-1 mb-8">
            {biz.tasks.map((t) => (
              <li key={t.id} className="flex items-center gap-3 py-2 group" data-testid={`task-${t.id}`}>
                <button onClick={() => toggleTask(t.id)} className={`transition-colors duration-200 ${t.done ? "text-emerald-400" : "text-zinc-500 hover:text-[#D4AF37]"}`} data-testid={`task-toggle-${t.id}`}>
                  {t.done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                </button>
                <span className={`text-sm flex-1 min-w-0 truncate transition-colors duration-200 ${t.done ? "text-zinc-600 line-through" : "text-zinc-200"}`}>{t.title}</span>
                <span className={`text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border ${PRIORITY_STYLE[t.priority || "medium"]}`}>
                  {t.priority || "medium"}
                </span>
                <span className={`text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border ${FREQ_CHIP[t.frequency] || FREQ_CHIP.weekly}`}>
                  {FREQ_LABEL[t.frequency] || t.frequency}
                </span>
                <button onClick={() => deleteTask(t.id)} className="text-zinc-600 hover:text-red-400 transition-colors duration-200 sm:opacity-0 sm:group-hover:opacity-100 sm:transition-opacity" data-testid={`task-delete-${t.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
          <form onSubmit={addTask} className="flex gap-2" data-testid="add-task-form">
            <Input
              value={newTask} onChange={(e) => setNewTask(e.target.value)} placeholder="Add a new win…"
              className="bg-white/5 border-white/10 text-white rounded-sm flex-1 focus-visible:ring-[#D4AF37]" data-testid="new-task-input"
            />
            <button
              type="button" onClick={() => setNewPriority(PRIORITY_CYCLE[(PRIORITY_CYCLE.indexOf(newPriority) + 1) % PRIORITY_CYCLE.length])}
              className={`text-[10px] uppercase tracking-widest px-3 border rounded-sm transition-colors duration-200 ${PRIORITY_STYLE[newPriority]}`}
              data-testid="task-priority-toggle"
            >
              {newPriority}
            </button>
            <button
              type="button" onClick={() => setNewFreq(FREQ_CYCLE[(FREQ_CYCLE.indexOf(newFreq) + 1) % FREQ_CYCLE.length])}
              className="text-[10px] uppercase tracking-widest px-3 border border-white/10 rounded-sm text-zinc-400 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors duration-200"
              data-testid="task-frequency-toggle"
            >
              {FREQ_LABEL[newFreq]}
            </button>
            <button type="submit" className="px-4 bg-[#D4AF37] text-black rounded-sm hover:bg-[#e5c555] transition-colors duration-200" data-testid="add-task-button">
              <Plus className="w-4 h-4" />
            </button>
          </form>
        </section>
      </div>

      {/* Update metrics */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <form onSubmit={saveMetrics} className="bg-[#0F0F11] border border-white/10 rounded-md p-8" data-testid="metrics-form">
          <div className="flex items-center gap-3 mb-6">
            <Save className="w-4 h-4 text-[#D4AF37]" />
            <h2 className="font-display text-2xl text-white" data-testid="metrics-form-heading">Update This Week's Numbers</h2>
          </div>
          <p className="text-xs text-zinc-500 mb-6 -mt-4">Leave blank to keep the current value. Live feeds from Xero, Google Analytics & Meta plug in here next.</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {[
              ["bank_balance", "Bank Balance (AUD)"],
              ["visitors", "Visitors"],
              ["ad_spend", "Ad Spend (AUD)"],
              ["reach", "Reach"],
              ["conversions", "Conversions"],
            ].map(([key, label], i) => (
              <div key={key} className={i === 0 ? "col-span-2 md:col-span-1" : ""}>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">{label}</label>
                <Input
                  type="number" min="0" value={metricForm[key]}
                  onChange={(e) => setMetricForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white rounded-sm focus-visible:ring-[#D4AF37]"
                  data-testid={`metric-input-${key}`}
                />
              </div>
            ))}
          </div>
          <button
            type="submit" disabled={saving}
            className="px-8 py-3 bg-[#D4AF37] text-black text-sm font-bold uppercase tracking-[0.2em] rounded-sm hover:bg-[#e5c555] transition-colors duration-200 disabled:opacity-50"
            data-testid="save-metrics-button"
          >
            {saving ? "Saving…" : "Save Numbers"}
          </button>
        </form>
      </section>
    </div>
  );
}
